import { buildAutoFreeGifts, getEffectiveMultiply } from '@/lib/promotions';
import { BaseFreeGift, usePromotionStore } from '@/stores/promotion';
import { BasePromotion } from '@/types/http/promotion/common';
import { useEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';

type UseAutoSpecificFreeGift = {
  listPromotion: BasePromotion[] | undefined;
  cartItems: CartItem[];
  grandTotal: number;
  preview?: boolean;
  isLoadingData?: boolean;
};

type UseAutoApplyPromo = {
  listPromotion?: BasePromotion[];
  recommendPromotion?: BasePromotion[];
  grandTotal: number;
  preview?: boolean;
  isLoadingData?: boolean;
  skipIfHasNonOverlapSelected?: boolean;
};

type CartItem = { item_id: string; qty: number; amount: number };

const ID_DAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'] as const;

const isDayAllowed = (promo: BasePromotion, now = new Date()): boolean => {
  if (!promo.applicable_day) return true;
  const m = promo.applicable_day.match(/\{([^}]*)\}/);
  if (!m) return true;
  const allowed = new Set(m[1].split(',').map((s) => s.trim()));
  const today = ID_DAYS[now.getDay()];
  return allowed.has(today);
};

const isTimeAllowed = (promo: BasePromotion, now = new Date()): boolean => {
  const slots = promo.availability ?? [];
  if (slots.length === 0) return true;
  const timeStr = now.toTimeString().slice(0, 8);
  return slots.some(
    (s) =>
      (s.start_time === '00:00:00' && s.end_time === '00:00:00') ||
      (s.start_time <= timeStr && timeStr <= s.end_time)
  );
};

function computeSpecificMultiply(promo: BasePromotion, cart: CartItem[]): number {
  const spec = promo.promotion_specific?.[0];
  if (!spec) return 0;

  const needQty = Math.max(1, spec.qty ?? 1);
  const eligibleIds = new Set(spec.item_promotion_specific?.map((x) => x.item_id) ?? []);
  if (eligibleIds.size === 0) return 0;

  const totalEligibleQty = cart.reduce(
    (sum, ci) => sum + (eligibleIds.has(ci.item_id) ? ci.qty : 0),
    0
  );
  const sets = Math.floor(totalEligibleQty / needQty);
  if (sets <= 0) return 0;

  const counted = sets;
  const maxMul = Math.max(0, promo.max_multiply ?? 0);
  return maxMul > 0 ? Math.min(counted, maxMul) : counted;
}

const makeGiftSignature = (promo: BasePromotion) => {
  const include = promo.product_reward?.find(
    (r) => r.reward_type === 'product' && r.rule_type === 'include'
  );
  const qty = (include?.qty ?? 0) * getEffectiveMultiply(promo);
  const items = (promo.applied_product_reward ?? [])
    .map((ap) => ap.item_id)
    .sort()
    .join(',');
  return `${promo.promotion_id}|qty:${qty}|items:${items}`;
};

const reconcileSelectedByGrandTotal = (
  current: BasePromotion[],
  listPromotion: BasePromotion[],
  grandTotal: number
) => {
  const removedIds: string[] = [];
  const replacedIds = new Set<string>();
  const byId = (id: string) =>
    listPromotion
      .filter((p) => p.promotion_id === id && p.minimum_spending <= grandTotal)
      .sort((a, b) => b.minimum_spending - a.minimum_spending)[0];

  const next: BasePromotion[] = [];

  for (const p of current) {
    if (p.minimum_spending <= grandTotal) {
      next.push(p);
      continue;
    }
    const eligibleLower = byId(p.promotion_id);
    if (eligibleLower) {
      next.push(eligibleLower);
      replacedIds.add(p.promotion_id);
    } else {
      removedIds.push(p.promotion_id);
    }
  }

  const dedup = new Map(next.map((n) => [n.promotion_id, n]));
  const nextSelected = Array.from(dedup.values());

  const changed =
    nextSelected.length !== current.length ||
    nextSelected.some(
      (n) =>
        !current.some(
          (c) => c.promotion_id === n.promotion_id && c.minimum_spending === n.minimum_spending
        )
    );

  return { changed, nextSelected, removedIds, replacedIds: Array.from(replacedIds) };
};

export function useAutoApplySpecificFreeGift({
  listPromotion,
  cartItems,
  grandTotal,
  preview,
  isLoadingData,
}: UseAutoSpecificFreeGift) {
  const {
    selectedPromotion,
    selectedFreeGift,
    addPromotion,
    applyPromotion,
    replaceFreeGift,
    removeFreeGift,
    syncFreeGiftPromotion,
  } = usePromotionStore(
    (s) => ({
      selectedPromotion: s.selectedPromotion,
      selectedFreeGift: s.selectedFreeGift,
      addPromotion: s.addPromotion,
      applyPromotion: s.applyPromotion,
      replaceFreeGift: s.replaceFreeGift,
      removeFreeGift: s.removeFreeGift,
      syncFreeGiftPromotion: s.syncFreeGiftPromotion,
    }),
    shallow
  );

  const runKey = useMemo(() => {
    const lp = (listPromotion ?? [])
      .map((p) => {
        const include = p.product_reward?.find(
          (r) => r.reward_type === 'product' && r.rule_type === 'include'
        );
        const includeQty = include?.qty ?? 0;
        const includeIds = (include?.reward_items ?? [])
          .map((it) => it.target_id)
          .sort()
          .join(',');
        const specQty = p.promotion_specific?.[0]?.qty ?? 0;
        const specIds = (p.promotion_specific?.[0]?.item_promotion_specific ?? [])
          .map((i) => i.item_id)
          .sort()
          .join(',');
        return [
          p.promotion_id,
          p.minimum_spending,
          p.max_multiply,
          includeQty,
          includeIds,
          specQty,
          specIds,
        ].join(':');
      })
      .sort()
      .join('|');
    const cart = cartItems
      .map((i) => `${i.item_id}:${i.qty}`)
      .sort()
      .join('|');
    return `${lp}__${cart}__${grandTotal}`;
  }, [listPromotion, cartItems, grandTotal]);

  const lockRef = useRef(false);
  const lastGiftSigRef = useRef<Map<string, string>>(new Map());
  const lastAppliedPromoIdsRef = useRef<Set<string>>(new Set());
  const prevEligibleRef = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    if (preview || isLoadingData) return;
    if (!listPromotion?.length) return;
    if (lockRef.current) return;
    lockRef.current = true;

    try {
      const selected = selectedPromotion;
      const allSelectedOverlapTrue =
        selected.length === 0 || selected.every((p) => p.is_overlap === true);

      // kandidat promo “specific” + reward include
      const candidates = listPromotion.filter(
        (p) =>
          (p.product_reward?.some(
            (r) => r.reward_type === 'product' && r.rule_type === 'include'
          ) ??
            false) &&
          (p.promotion_specific?.length ?? 0) > 0 &&
          p.is_not_expired &&
          isDayAllowed(p) &&
          isTimeAllowed(p)
      );

      // promos yang eligible sekarang (punya multiply >=1)
      const eligible: BasePromotion[] = [];
      for (const promo of candidates) {
        const sets = computeSpecificMultiply(promo, cartItems);
        const nowEligible = sets > 0;

        prevEligibleRef.current.set(promo.promotion_id, nowEligible);

        if (nowEligible) {
          eligible.push({ ...promo, counted_multiply: sets });
        }
      }

      // APPLY promos (respect overlap) — update hanya kalau berubah
      const nextIds = new Set(eligible.map((p) => p.promotion_id));
      const prevIds = lastAppliedPromoIdsRef.current;

      // remove gift untuk promo yang kini tidak eligible
      prevIds.forEach((id) => {
        if (!nextIds.has(id)) {
          removeFreeGift(id);
          lastGiftSigRef.current.delete(id);
        }
      });

      // untuk setiap eligible promo: apply & inject gift IDEMPOTENT
      for (const promo of eligible) {
        const alreadySame = selected.some(
          (p) =>
            p.promotion_id === promo.promotion_id &&
            (p.counted_multiply ?? 0) === (promo.counted_multiply ?? 0)
        );

        if (!alreadySame) {
          if (allSelectedOverlapTrue && promo.is_overlap === true) {
            addPromotion(promo, grandTotal, listPromotion);
          } else {
            applyPromotion(promo);
          }
        }

        // gift signature check
        const sig = makeGiftSignature(promo);
        const lastSig = lastGiftSigRef.current.get(promo.promotion_id);

        const currentGiftsForPromo = selectedFreeGift.filter(
          (g) => g.promotion_id === promo.promotion_id
        );

        const wasEligible = prevIds.has(promo.promotion_id);
        const transitionedToEligible = !wasEligible && nextIds.has(promo.promotion_id);

        const needInject =
          transitionedToEligible || currentGiftsForPromo.length === 0 || sig !== lastSig;

        if (needInject) {
          const gifts = buildAutoFreeGifts(promo);
          if (gifts.length > 0) {
            replaceFreeGift(promo.promotion_id, gifts as unknown as BaseFreeGift[], 'replace');
            lastGiftSigRef.current.set(promo.promotion_id, sig);
          }
        }
      }

      // update cache promo ids yang lagi aktif auto-apply
      lastAppliedPromoIdsRef.current = nextIds;

      // cleanup orphan gift
      syncFreeGiftPromotion();
    } finally {
      lockRef.current = false;
    }
    // DEPEND hanya runKey (input eksternal), bukan state store → tidak loop
  }, [
    runKey,
    preview,
    isLoadingData,
    listPromotion,
    cartItems,
    grandTotal,
    addPromotion,
    applyPromotion,
    replaceFreeGift,
    removeFreeGift,
    syncFreeGiftPromotion,
  ]);
}

export function useAutoApplyBestSpendingPromo({
  listPromotion,
  recommendPromotion,
  grandTotal,
  preview,
  isLoadingData,
  skipIfHasNonOverlapSelected = true,
}: UseAutoApplyPromo) {
  const {
    selectedPromotion,
    addPromotion,
    applyPromotion,
    replaceFreeGift,
    removeFreeGift,
    syncFreeGiftPromotion,
  } = usePromotionStore(
    (s) => ({
      selectedPromotion: s.selectedPromotion,
      addPromotion: s.addPromotion,
      applyPromotion: s.applyPromotion,
      replaceFreeGift: s.replaceFreeGift,
      removeFreeGift: s.removeFreeGift,
      syncFreeGiftPromotion: s.syncFreeGiftPromotion,
    }),
    shallow
  );

  // runKey hanya berubah saat input eksternal berubah (bukan karena store update)
  const runKey = useMemo(() => {
    const lp = (listPromotion ?? [])
      .map((p) => [p.promotion_id, p.minimum_spending, p.max_multiply].join(':'))
      .sort()
      .join('|');
    const rp = (recommendPromotion ?? [])
      .map((p) => [p.promotion_id, p.minimum_spending].join(':'))
      .sort()
      .join('|');
    return `${lp}__${rp}__${grandTotal}`;
  }, [listPromotion, recommendPromotion, grandTotal]);

  const lockRef = useRef(false);
  const lastGiftSigRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (preview || isLoadingData) return;
    if (!listPromotion?.length && !recommendPromotion?.length) return;
    if (lockRef.current) return;
    lockRef.current = true;

    try {
      const selected = selectedPromotion;

      if (skipIfHasNonOverlapSelected && selected.some((p) => p.is_overlap === false)) {
        return;
      }

      // 1) Pilih promo recommended dulu, kalau tidak ada ambil fallback (minimum_spending tertinggi yang <= grandTotal)
      const rec = (recommendPromotion ?? []).find((p) => grandTotal >= p.minimum_spending);
      const fallback = (listPromotion ?? [])
        .filter((p) => grandTotal >= p.minimum_spending)
        .sort((a, b) => b.minimum_spending - a.minimum_spending)[0];

      const promoToApply = rec || fallback;
      if (!promoToApply) return;

      // 2) Apply promo (respect overlap); kalau sudah sama, skip
      const allSelectedOverlapTrue =
        selected.length === 0 || selected.every((p) => p.is_overlap === true);
      const alreadySame = selected.some((p) => p.promotion_id === promoToApply.promotion_id);
      if (!alreadySame) {
        if (allSelectedOverlapTrue && promoToApply.is_overlap === true) {
          addPromotion(promoToApply, grandTotal, listPromotion);
        } else {
          applyPromotion(promoToApply);
        }
      }

      // 3) Auto free gift (idempotent replace per promo)
      const hasApplied = (promoToApply.applied_product_reward?.length ?? 0) > 0;
      const hasInclude = (promoToApply.product_reward ?? []).some(
        (r) => r.reward_type === 'product' && r.rule_type === 'include'
      );
      if (hasApplied && hasInclude) {
        const sig = makeGiftSignature(promoToApply);
        const lastSig = lastGiftSigRef.current.get(promoToApply.promotion_id);
        if (sig !== lastSig) {
          const gifts = buildAutoFreeGifts(promoToApply);
          if (gifts.length > 0) {
            // REPLACE per promo → qty tidak numpuk; promo overlap lain tetap aman
            replaceFreeGift(promoToApply.promotion_id, gifts as any, 'replace');
            lastGiftSigRef.current.set(promoToApply.promotion_id, sig);
          }
        }
      } else {
        removeFreeGift(promoToApply.promotion_id);
      }

      // 4) Rapikan orphan di akhir
      syncFreeGiftPromotion();
    } finally {
      lockRef.current = false;
    }
  }, [
    runKey,
    preview,
    isLoadingData,
    listPromotion,
    recommendPromotion,
    grandTotal,
    addPromotion,
    applyPromotion,
    replaceFreeGift,
    syncFreeGiftPromotion,
  ]);
}

export function usePruneSpendingPromos({
  listPromotion,
  grandTotal,
  preview,
  isLoadingData,
}: {
  listPromotion?: BasePromotion[];
  grandTotal: number;
  preview?: boolean;
  isLoadingData?: boolean;
}) {
  const { selectedPromotion, setSelectedPromotion, removeFreeGift, syncFreeGiftPromotion } =
    usePromotionStore(
      (s) => ({
        selectedPromotion: s.selectedPromotion,
        setSelectedPromotion: s.setSelectedPromotion,
        removeFreeGift: s.removeFreeGift,
        syncFreeGiftPromotion: s.syncFreeGiftPromotion,
      }),
      shallow
    );

  const runKey = useMemo(() => {
    const lp = (listPromotion ?? [])
      .map((p) => `${p.promotion_id}:${p.minimum_spending}`)
      .sort()
      .join('|');
    return `${lp}__${grandTotal}`;
  }, [listPromotion, grandTotal]);

  const locking = useRef(false);

  useEffect(() => {
    if (preview || isLoadingData || !listPromotion?.length) return;
    if (locking.current) return;
    locking.current = true;

    try {
      if (!selectedPromotion.length) return;

      const { changed, nextSelected, removedIds, replacedIds } = reconcileSelectedByGrandTotal(
        selectedPromotion,
        listPromotion,
        grandTotal
      );

      if (!changed) return;

      setSelectedPromotion(nextSelected);

      for (const id of removedIds) removeFreeGift(id);
      for (const id of replacedIds) removeFreeGift(id);

      syncFreeGiftPromotion();
    } finally {
      locking.current = false;
    }
  }, [
    runKey,
    preview,
    isLoadingData,
    listPromotion,
    grandTotal,
    setSelectedPromotion,
    removeFreeGift,
    syncFreeGiftPromotion,
  ]);
}
